import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { executiveService } from '../../services/executiveService';
import { qrCodeManagementService, QRCodeStats } from '../../services/qrCodeManagementService';
import { Users, ShoppingBag, TrendingUp, Store, QrCode, UserCheck, ArrowRight, DollarSign, PiggyBank } from 'lucide-react';


interface NavigationCard {
  title: string;
  description: string;
  value: string;
  icon: any;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red';
  route: string;
}

const ExecutiveOverview: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<any>(null);
  const [qrStats, setQrStats] = useState<QRCodeStats | null>(null);

  // Load essential metrics only
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [metricsData, qrStatsData] = await Promise.all([
        executiveService.getDashboardMetrics(),
        qrCodeManagementService.getQRCodeStats()
      ]);
      setMetrics(metricsData);
      setQrStats(qrStatsData);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleCardClick = (route: string) => {
    navigate(route);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Dashboard</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button 
            onClick={loadDashboardData}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Define navigation cards based on loaded metrics
  const navigationCards: NavigationCard[] = [
    {
      title: 'User Analytics',
      description: 'Detailed user metrics and demographics',
      value: metrics?.totalUsers ? `${metrics.totalUsers.toLocaleString()} Total Users` : '...',
      icon: Users,
      color: 'blue',
      route: '/user-management'
    },
    {
      title: 'Offer Management',
      description: 'Manage and optimize platform offers',
      value: metrics?.totalOffers ? `${metrics.totalOffers.toLocaleString()} Active Offers` : '...',
      icon: ShoppingBag,
      color: 'green',
      route: '/offer-management'
    },
    {
      title: 'Redemption Analytics',
      description: 'Track redemptions and user engagement',
      value: metrics?.totalRedemptions ? `${metrics.totalRedemptions.toLocaleString()} Redemptions` : '...',
      icon: TrendingUp,
      color: 'purple',
      route: '/redemption-analytics'
    },
    {
      title: 'Partner Management',
      description: 'Oversee partner relationships and performance',
      value: metrics?.activePartnersCount ? `${metrics.activePartnersCount.toLocaleString()} Active Partners` : '...',
      icon: Store,
      color: 'orange',
      route: '/partner-management-dashboard'
    },
    {
      title: 'QR Code Management',
      description: 'Monitor activation codes and distribution',
      value: qrStats?.totalQRCodes ? `${qrStats.totalQRCodes.toLocaleString()} QR Codes` : '...',
      icon: QrCode,
      color: 'blue',
      route: '/qr-code-management'
    },
    {
      title: 'User Activation',
      description: 'Track user activation and onboarding',
      value: qrStats?.activationRate ? `${qrStats.activationRate.toFixed(1)}% Activation Rate` : '...',
      icon: UserCheck,
      color: 'green',
      route: '/user-management'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Executive Overview - TravelHeroes Admin Dashboard</title>
        <meta name="description" content="Strategic overview and navigation hub for TravelHeroes platform" />
      </Helmet>

      <div className="p-6 lg:p-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Executive Overview</h1>
          <p className="text-muted-foreground mt-2">
            Strategic platform overview with quick navigation to detailed analytics
          </p>
          {loading && (
            <div className="mt-2 text-sm text-blue-600">
              Loading platform metrics...
            </div>
          )}
        </div>

        {/* Key Metrics Strip */}
        {metrics && qrStats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {metrics?.totalUsers?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-muted-foreground">Total Users</div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <UserCheck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {qrStats?.activatedUsers?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-muted-foreground">Activated Users</div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {metrics?.totalRedemptions?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-muted-foreground">Redemptions</div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Store className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {metrics?.activePartnersCount?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-muted-foreground">Active Partners</div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    €{((metrics?.totalPartnerRevenue || 0) / 1000).toFixed(1)}K
                  </div>
                  <div className="text-xs text-muted-foreground">Partner Revenue</div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-pink-100 rounded-lg">
                  <PiggyBank className="h-5 w-5 text-pink-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    €{((metrics?.totalSavings || 0) / 1000).toFixed(1)}K
                  </div>
                  <div className="text-xs text-muted-foreground">Total Savings</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {navigationCards.map((card) => (
            <div
              key={card.route}
              onClick={() => handleCardClick(card.route)}
              className="group bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-all duration-200 cursor-pointer hover:border-primary"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${
                  card.color === 'blue' ? 'bg-blue-100' :
                  card.color === 'green' ? 'bg-green-100' :
                  card.color === 'purple' ? 'bg-purple-100' :
                  card.color === 'orange'? 'bg-orange-100' : 'bg-red-100'
                }`}>
                  <card.icon className={`h-6 w-6 ${
                    card.color === 'blue' ? 'text-blue-600' :
                    card.color === 'green' ? 'text-green-600' :
                    card.color === 'purple' ? 'text-purple-600' :
                    card.color === 'orange'? 'text-orange-600' : 'text-red-600'
                  }`} />
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>

              <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                {card.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {card.description}
              </p>
              
              <div className="pt-3 border-t border-border">
                <div className="text-xl font-bold text-foreground">
                  {loading ? '...' : card.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Stats Footer */}
        {!loading && qrStats && metrics && (
          <div className="mt-8 p-6 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">
                  {qrStats?.activationRate?.toFixed(1) || '0.0'}%
                </div>
                <div className="text-sm text-muted-foreground">Activation Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">
                  €{((metrics?.totalPartnerRevenue || 0) / 1000).toFixed(1)}K
                </div>
                <div className="text-sm text-muted-foreground">Partner Revenue</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">
                  €{((metrics?.totalSavings || 0) / 1000).toFixed(1)}K
                </div>
                <div className="text-sm text-muted-foreground">Total Savings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">
                  {metrics?.activePartnersCount?.toLocaleString() || '0'}
                </div>
                <div className="text-sm text-muted-foreground">Active Partners</div>
              </div>
            </div>
          </div>
        )}

        {/* Last Updated Info */}
        {!loading && (
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Data updated: {new Date().toLocaleTimeString()} • Source: Live Supabase Analytics
          </div>
        )}
      </div>
    </>
  );
};

export default ExecutiveOverview;